import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Search, X } from "lucide-react";
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  entityCardTypeLabelKeys,
  entityCardTypes,
  hasActiveEntityFilters,
  type EntityLibraryFilters,
} from '@/features/entities'

type EntityLibraryFilterBarProps = {
  filters: EntityLibraryFilters
  onClearFilters: () => void
  onSearchTextChange: (value: string) => void
  onTypeFilterChange: (value: EntityLibraryFilters['typeFilter']) => void
  resultCount: number
  totalCount: number
}

const ENTITY_FILTER_EXPANDED_STORAGE_KEY = "entities:filters-expanded";

function readExpandedState() {
  if (typeof window === "undefined") {
    return true;
  }

  const storedValue = window.localStorage.getItem(ENTITY_FILTER_EXPANDED_STORAGE_KEY);
  return storedValue === null ? true : storedValue === "true";
}

function getLibrarySummaryText(
  filters: EntityLibraryFilters,
  resultCount: number,
  totalCount: number,
  t: (key: string, options?: Record<string, unknown>) => string,
) {
  if (totalCount === 0) {
    return t('entities.filters.summaryEmpty')
  }

  if (!hasActiveEntityFilters(filters)) {
    return t('entities.filters.summaryVisible', {
      resultCount,
      totalCount,
    })
  }

  return t('entities.filters.summaryFiltered', {
    resultCount,
    totalCount,
  })
}

export function EntityLibraryFilterBar({
  filters,
  onClearFilters,
  onSearchTextChange,
  onTypeFilterChange,
  resultCount,
  totalCount,
}: EntityLibraryFilterBarProps) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(readExpandedState);
  const filtersActive = hasActiveEntityFilters(filters)
  const summaryText = getLibrarySummaryText(filters, resultCount, totalCount, t);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(ENTITY_FILTER_EXPANDED_STORAGE_KEY, String(expanded));
  }, [expanded]);

  return (
    <section className="space-y-4 border-t border-ink/10 pt-6">
      <button
        className="flex w-full items-center justify-between gap-3 text-left"
        onClick={() => setExpanded((current) => !current)}
        type="button"
      >
        <div className="space-y-1">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">
            {t('entities.filters.title')}
          </p>
          <p className="text-xs text-muted-foreground">{summaryText}</p>
        </div>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </button>

      {expanded ? (
        <>
          <div className="relative">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              aria-label={t('entities.filters.searchLabel')}
              className="pl-11"
              onChange={(event) => onSearchTextChange(event.target.value)}
              placeholder={t('entities.filters.searchPlaceholder')}
              value={filters.searchText}
            />
          </div>

          <Select
            onValueChange={(value) =>
              onTypeFilterChange(value as EntityLibraryFilters['typeFilter'])
            }
            value={filters.typeFilter}
          >
            <SelectTrigger aria-label={t('entities.filters.typeLabel')}>
              <SelectValue placeholder={t('entities.filters.allTypes')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('entities.filters.allTypes')}</SelectItem>
              {entityCardTypes.map((type) => (
                <SelectItem key={type} value={type}>
                  {t(entityCardTypeLabelKeys[type])}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            className="h-auto rounded-full px-0 py-0 text-xs font-semibold uppercase tracking-[0.16em] text-clay hover:bg-transparent hover:text-foreground"
            disabled={!filtersActive}
            onClick={onClearFilters}
            type="button"
            variant="ghost"
          >
            <X className="h-3.5 w-3.5" />
            {t('entities.filters.clear')}
          </Button>
        </>
      ) : null}
    </section>
  )
}
